/**
 * Is this actually a song?
 *
 * Beseter hosts songs. The commonest bad upload is not malicious, it is someone
 * sending a voice memo of themselves explaining a song instead of singing it —
 * and the second commonest is someone treating the upload box as a message box.
 * Both are worth catching before a pastor spends four minutes finding out.
 *
 * Transcription is the obvious tool for this and it is the wrong one. Speech
 * recognition is trained on speech; on singing it fails on sustained vowels,
 * melisma and backing instruments. So a clean transcript means *talking* and a
 * garbled one means *singing*, and you end up inferring music from the failure
 * of an API call. Worse, the Web Speech API cannot read a File at all — it only
 * listens to a live microphone, so scoring a four minute upload would take four
 * minutes of playing it aloud.
 *
 * What actually separates singing from speech is pitch behaviour, and that is
 * measurable directly from the samples:
 *
 *   sustain   Singing holds a pitch still for hundreds of milliseconds. Speech
 *             pitch glides continuously and never settles. This is the single
 *             strongest signal and it is weighted accordingly.
 *   tuning    Sung notes land on a twelve-tone grid. Speech intonation has no
 *             reason to prefer semitones over anything else.
 *   pulse     Music has a beat. An explanation does not.
 *
 * No model, no API key, no network, no per-upload cost, and it works offline.
 *
 * IT IS ADVISORY AND IT IS SPOOFABLE. The browser computes it and the browser
 * is the attacker's machine, so a score may *raise* suspicion but must never
 * clear it. Rap over a sparse beat, spoken-word liturgy and unaccompanied
 * chant all score low while being perfectly legitimate — which is exactly why
 * a low score routes to a moderator instead of blocking the writer.
 */

/** Below this, ask a human (or a model that can listen). Tuned to be forgiving. */
export const SUSPECT_BELOW = 0.45

/**
 * Analysis runs at 8 kHz. Nyquist is then 4 kHz, far above any sung fundamental
 * — we need f0 up to ~1 kHz, not the harmonics — and dropping the rate is what
 * turns an O(frames x lags x window) search into something that finishes while
 * the file is still uploading.
 */
const RATE = 8000
const F_MIN = 70    // below a bass voice
const F_MAX = 1000  // above a soprano
const FRAME = 800   // 100 ms — two full cycles at F_MIN
const HOP = 400     // 50 ms
const MAX_SECONDS = 45 // enough to characterise a take without scanning a whole album

/** McLeod peak below this is noise, not a pitch. */
const VOICED = 0.55
/** A pitch that stays inside this many cents is "the same note held". */
const SAME_NOTE_CENTS = 60
/** Frames a note must hold to count as sustained: 3 hops = 150 ms. */
const SUSTAIN_FRAMES = 3

const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n)

/**
 * Downmix to mono and decimate to RATE with a box filter.
 *
 * The box filter is the cheapest anti-alias there is and it is genuinely not
 * good enough for audio you intend to listen to. It is fine here: everything
 * downstream reads periodicity below 1 kHz, and the aliasing lands above that.
 */
function toMono8k(buffer) {
  const chans = Math.min(buffer.numberOfChannels, 2)
  const src = buffer.getChannelData(0)
  const src2 = chans > 1 ? buffer.getChannelData(1) : null

  const factor = Math.max(1, Math.round(buffer.sampleRate / RATE))
  const wanted = Math.min(src.length, Math.floor(buffer.sampleRate * MAX_SECONDS))

  // Skip a leading count-in or silence rather than letting it dominate.
  const start = Math.min(
    Math.floor(buffer.sampleRate * 0.5),
    Math.max(0, src.length - wanted),
  )

  const outLen = Math.floor((wanted - start) / factor)
  const out = new Float32Array(outLen > 0 ? outLen : 0)

  for (let i = 0; i < outLen; i++) {
    const from = start + i * factor
    let sum = 0
    for (let k = 0; k < factor; k++) {
      const j = from + k
      sum += src2 ? (src[j] + src2[j]) * 0.5 : src[j]
    }
    out[i] = sum / factor
  }
  return out
}

/**
 * Pitch for one frame by the McLeod method: a normalised square difference
 * function, whose peaks sit at multiples of the period but whose *first*
 * strong peak is the true one. Picking the first peak above a fraction of the
 * global maximum, rather than the maximum itself, is what stops it reporting
 * an octave too low.
 *
 * Returns { hz, clarity } and clarity 0 when nothing is periodic enough.
 */
function pitchOf(x, offset) {
  const minLag = Math.floor(RATE / F_MAX)
  const maxLag = Math.min(Math.floor(RATE / F_MIN), FRAME - 1)

  const nsdf = new Float32Array(maxLag + 1)
  for (let lag = minLag; lag <= maxLag; lag++) {
    let ac = 0
    let norm = 0
    const n = FRAME - lag
    for (let i = 0; i < n; i++) {
      const a = x[offset + i]
      const b = x[offset + i + lag]
      ac += a * b
      norm += a * a + b * b
    }
    nsdf[lag] = norm > 0 ? (2 * ac) / norm : 0
  }

  let best = 0
  for (let lag = minLag; lag <= maxLag; lag++) if (nsdf[lag] > best) best = nsdf[lag]
  if (best < VOICED) return { hz: 0, clarity: 0 }

  // First peak that clears most of the global maximum — the fundamental.
  const gate = best * 0.9
  let chosen = -1
  for (let lag = minLag + 1; lag < maxLag; lag++) {
    if (nsdf[lag] > nsdf[lag - 1] && nsdf[lag] >= nsdf[lag + 1] && nsdf[lag] >= gate) {
      chosen = lag
      break
    }
  }
  if (chosen < 0) return { hz: 0, clarity: 0 }

  // Parabolic interpolation: without it every pitch snaps to an integer lag,
  // which at 8 kHz is up to 30 cents of error — enough to destroy the tuning
  // measurement below, which is looking for deviations of exactly that size.
  const y0 = nsdf[chosen - 1]
  const y1 = nsdf[chosen]
  const y2 = nsdf[chosen + 1]
  const denom = 2 * (2 * y1 - y0 - y2)
  const shift = denom !== 0 ? (y2 - y0) / denom : 0
  const lag = chosen + Math.max(-1, Math.min(1, shift))

  return { hz: RATE / lag, clarity: y1 }
}

/**
 * How much of the voiced material is held still.
 *
 * Walks the pitch track and grows a run while the pitch stays within
 * SAME_NOTE_CENTS of where the run began. Speech breaks these runs almost
 * immediately; a sung phrase produces long ones.
 */
function sustainOf(cents) {
  let held = 0
  let voiced = 0
  let runStart = -1
  let runLen = 0

  const closeRun = () => {
    if (runLen >= SUSTAIN_FRAMES) held += runLen
    runStart = -1
    runLen = 0
  }

  for (let i = 0; i < cents.length; i++) {
    const c = cents[i]
    if (c === null) { closeRun(); continue }
    voiced++
    if (runStart < 0 || Math.abs(c - runStart) > SAME_NOTE_CENTS) {
      closeRun()
      runStart = c
      runLen = 1
    } else {
      runLen++
    }
  }
  closeRun()

  return { sustain: voiced > 0 ? held / voiced : 0, voicedFrames: voiced }
}

/**
 * Do the notes sit on a twelve-tone grid?
 *
 * The reference pitch is unknown — the recording may be a semitone flat, or
 * tuned to something other than A440 — so the grid offset is searched rather
 * than assumed. What matters is whether the notes agree *with each other*.
 *
 * Uniformly random pitches average 25 cents from the nearest grid line, so that
 * is the zero point of the scale.
 */
function tuningOf(cents) {
  const notes = cents.filter((c) => c !== null)
  if (notes.length < 12) return 0

  let bestDev = Infinity
  for (let offset = 0; offset < 100; offset += 4) {
    let total = 0
    for (let i = 0; i < notes.length; i++) {
      let d = (((notes[i] - offset) % 100) + 100) % 100
      if (d > 50) d = 100 - d
      total += d
    }
    const dev = total / notes.length
    if (dev < bestDev) bestDev = dev
  }
  return clamp01(1 - bestDev / 25)
}

/**
 * Is there a pulse?
 *
 * Autocorrelates the energy envelope over lags matching 40–200 BPM. A backing
 * track or a strummed guitar produces a clear peak; continuous speech does not.
 *
 * Weighted lightly in the final score, because an unaccompanied voice is still
 * a song and scores near zero here.
 */
function pulseOf(env) {
  const minLag = Math.max(2, Math.round((60 / 200) * (RATE / HOP)))
  const maxLag = Math.round((60 / 40) * (RATE / HOP))
  if (env.length < maxLag * 2) return 0

  let mean = 0
  for (let i = 0; i < env.length; i++) mean += env[i]
  mean /= env.length

  const dev = new Float32Array(env.length)
  let energy = 0
  for (let i = 0; i < env.length; i++) {
    dev[i] = env[i] - mean
    energy += dev[i] * dev[i]
  }
  if (energy <= 0) return 0

  let best = 0
  for (let lag = minLag; lag <= maxLag; lag++) {
    let ac = 0
    for (let i = 0; i + lag < dev.length; i++) ac += dev[i] * dev[i + lag]
    const norm = ac / energy
    if (norm > best) best = norm
  }
  return clamp01(best)
}

/**
 * Score an already-decoded AudioBuffer.
 *
 * Returns null when there is too little audible material to judge — an empty
 * or near-silent file is a different problem, and reporting 0 for it would put
 * "not music" in front of a moderator when the truth is "not anything".
 */
export async function analyseBuffer(buffer, onProgress) {
  const x = toMono8k(buffer)
  if (x.length < FRAME * 4) return null

  const frames = Math.floor((x.length - FRAME) / HOP)
  const cents = []
  const env = new Float32Array(frames)
  let loud = 0

  for (let f = 0; f < frames; f++) {
    const at = f * HOP

    let rms = 0
    for (let i = 0; i < FRAME; i++) rms += x[at + i] * x[at + i]
    rms = Math.sqrt(rms / FRAME)
    env[f] = rms

    // -46 dBFS. Below this a "pitch" is the noise floor's opinion.
    if (rms < 0.005) { cents.push(null); continue }
    loud++

    const { hz, clarity } = pitchOf(x, at)
    cents.push(clarity > 0 && hz >= F_MIN && hz <= F_MAX ? 1200 * Math.log2(hz / 55) : null)

    // Yield so a phone stays responsive; this runs beside the encoder.
    if (f % 64 === 0) {
      onProgress?.(Math.round((f / frames) * 100))
      await new Promise((r) => setTimeout(r, 0))
    }
  }

  if (loud < frames * 0.1) return null // effectively silent

  const { sustain, voicedFrames } = sustainOf(cents)
  if (voicedFrames < 20) return null // nothing pitched to reason about

  const tuning = tuningOf(cents)
  const pulse = pulseOf(env)

  // Sustain carries the weight because it is the feature that actually
  // separates the two cases. Tuning corroborates it. Pulse is a bonus, not a
  // requirement, or every solo hymn would fail.
  const confidence = clamp01(0.55 * sustain + 0.3 * tuning + 0.15 * pulse)
  onProgress?.(100)

  return {
    confidence: Math.round(confidence * 100) / 100,
    suspect: confidence < SUSPECT_BELOW,
    features: {
      sustain: Math.round(sustain * 100) / 100,
      tuning: Math.round(tuning * 100) / 100,
      pulse: Math.round(pulse * 100) / 100,
      voicedRatio: Math.round((voicedFrames / frames) * 100) / 100,
    },
  }
}

/**
 * Score a File that has not been decoded yet.
 *
 * Used for uploads that skip compression because they are already compressed,
 * and for the audio track of a video. decodeAudioData handles mp4/webm audio in
 * most browsers but not reliably in all of them, so a decode failure returns
 * null — unknown, not guilty. A file we could not read must never become a flag.
 */
export async function analyseFile(file, onProgress) {
  const Ctx = window.AudioContext || window.webkitAudioContext
  if (!Ctx) return null
  const ctx = new Ctx()
  try {
    const decoded = await ctx.decodeAudioData(await file.arrayBuffer())
    return await analyseBuffer(decoded, onProgress)
  } catch {
    return null
  } finally {
    ctx.close?.()
  }
}
